import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiDeletePolicyRenewalFlagMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { RenewalFlagEnum } from '@/lib/heyapi/types.gen';

import { formatErrorMessage } from '../../../utils';
import type { Message } from '../../StandardMessage';

export const MarkAsRenewable = ({
  accountId,
  isOpen,
  onOpenChange,
  setMessage,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
}) => {
  const queryClient = useQueryClient();

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const { mutate: markAsRenewable, isPending: isMarkingAsRenewable } =
    useMutation({
      ...glacierPolicyApiDeletePolicyRenewalFlagMutation(),
      onSuccess() {
        setMessage(undefined);
        queryClient.refetchQueries({ queryKey: policyQK });
        onOpenChange(false);
        toast({
          title: 'Policy Marked as Renewable',
          description: (
            <div>
              <p>Policy marked as renewable successfully</p>
            </div>
          ),
        });
      },
      onError(error) {
        setMessage(undefined);
        onOpenChange(false);
        toast({
          title: 'Error Marking Policy as Renewable',
          description: formatErrorMessage(
            error,
            'An error occurred while marking the policy as renewable'
          ),
        });
      },
    });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as Renewable</DialogTitle>
          <DialogDescription>
            Marking the policy as renewable will allow the system to
            automatically create a renewal for this account. Do you wish to
            continue?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMarkingAsRenewable}
          >
            Cancel
          </Button>
          <Button
            disabled={isMarkingAsRenewable}
            loading={isMarkingAsRenewable}
            onClick={() => {
              setMessage({
                content: 'Marking policy as renewable...',
                level: 'INFO',
              });
              markAsRenewable({
                path: {
                  account_id: accountId,
                  flag: RenewalFlagEnum.FLAG_FOR_NON_RENEWAL,
                },
              });
            }}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
